import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useDucatClient } from '@/context/DucatClientCtx';
import { quantaHeadersBelongToWallet } from '@/hooks/api/quanta/quantaIdentity';
import { retained_liquidation_price } from '@/lib/vault_metrics';
import { resolve_vault_protocol } from '@/lib/vault-protocol';
import { canPerformVaultActions } from '@/lib/wallet-policy';
import { useAppStore, useConfig } from '@/stores/useAppStore';
import { selectActiveVault, useVaultSelectionStore, vaultSelectionScope } from '@/stores/useVaultSelectionStore';
import { toBtc } from '@/utils/amount.utils';

import { useQueryAccountVaultList } from '../wallet/useQueryAccountVaultList';

export function useVaultProfile({ purpose }: { purpose?: 'quanta' } = {}) {
  const { wallet, walletType, accountRevision, contract } = useDucatClient();
  const config = useConfig();
  const headers = useAppStore((s) => s.quantaHeaders);
  // Legacy Quanta missions retain their authenticated reward lookup. Personal
  // vault screens, the header and sidebar never opt into that separate purpose.
  const canLoadVault =
    canPerformVaultActions(walletType) || (purpose === 'quanta' && quantaHeadersBelongToWallet(headers, wallet, accountRevision));
  const vaultPubkey = canLoadVault ? wallet?.account.vault.pubkey : undefined;

  // Vault info data from validator:
  const { data: accountVaultList, isLoading, refetch } = useQueryAccountVaultList(vaultPubkey);

  const scope = vaultSelectionScope(config?.validatorUrl, config?.network, vaultPubkey);
  const selectedRoot = useVaultSelectionStore((state) => (scope ? state.roots[scope] : undefined));
  const select = useVaultSelectionStore((state) => state.select);
  const activeVaults = useMemo(() => accountVaultList?.filter((vault) => vault.coin_id != null) ?? [], [accountVaultList]);
  const validatorVault = selectActiveVault(activeVaults, selectedRoot);
  const root = validatorVault?.root_txid;
  const current = useRef({ scope, root });
  current.current = { scope, root };
  // Pin the initial choice so recovery or validator ordering cannot switch it.
  // A closed vault falls back to the next active vault on the dashboard only.
  useEffect(() => {
    if (scope && root && root !== selectedRoot) select(scope, root);
  }, [scope, root, selectedRoot, select]);

  const tag = validatorVault?.vault_config?.label;
  const vaultTxId = validatorVault?.root_txid;
  const unit_balance = validatorVault?.unit_balance || 0;
  const satsInVault = validatorVault?.vault_balance || 0;
  const satsInVaultDeposited = validatorVault?.vault_value || 0;

  const btcInVault = toBtc(satsInVault);
  const btcInVaultDeposited = toBtc(satsInVaultDeposited);

  // Unit dollars in vault:
  const unitInVault = unit_balance / 100;

  const history = useQuery({
    queryKey: [
      'vault/protocol',
      config?.validatorUrl,
      contract?.chain_network,
      contract?.anchor_id,
      contract?.contract_id,
      validatorVault?.contract_id,
    ],
    enabled: !!config && !!contract && !!validatorVault && validatorVault.contract_id !== contract.contract_id,
    queryFn: async () => {
      if (!config || !contract || !validatorVault) throw new Error('Vault protocol context unavailable');
      return resolve_vault_protocol(contract, validatorVault, config);
    },
    staleTime: 5 * 60 * 1000,
  });
  const vaultProtocol = validatorVault?.contract_id === contract?.contract_id ? contract : history.data;
  const liquidationPrice = useMemo(() => retained_liquidation_price(validatorVault, vaultProtocol), [validatorVault, vaultProtocol]);

  return {
    canLoadVault,
    vaultProfile: validatorVault,
    activeVaults,
    selectVault: (nextRoot: string) => {
      if (scope && current.current.scope === scope && activeVaults.some((vault) => vault.root_txid === nextRoot)) select(scope, nextRoot);
    },
    refetchVaultProfile: async () => {
      const assertSelected = () => {
        if (!root || current.current.scope !== scope || current.current.root !== root)
          throw Error('Selected vault changed. Please try again.');
      };
      // The query also checks wallet eligibility and observer lifetime.
      const result = await refetch({ throwOnError: true });
      assertSelected();
      const vaultProfile = result.data?.find((vault) => vault.coin_id != null && vault.root_txid === root);
      if (!vaultProfile) {
        throw Error('Failed to refetch vault data');
      }

      return vaultProfile;
    },
    tag,
    vaultPubkey,
    vaultTxId,
    isLoading,
    unit_balance,
    satsInVault,
    satsInVaultDeposited,
    btcInVault,
    btcInVaultDeposited,
    unitInVault,
    liquidationPrice,
  };
}
